import api from "@/services/api";
import { OutcomeItem, fetchOutcome, fetchPaymentCategoryMaps } from "@/services/financeiro";
import { FilterValues } from "@/components/ui/FilterBar/types";
import { currentLocalDateInput } from "@/utils/dateInput";

/* ─── Tipos ──────────────────────────────────────────────────────────────── */

export interface UnitsByCCItem {
  costCenterId:   string;
  costCenterName: string;
  unitCount:      number;
}

export interface GerencialData {
  outcome: OutcomeItem[];
  unitsByCC: UnitsByCCItem[];
  /** Código Sienge por financialCategoryId — para exibir no modal */
  categoryCodesById: Map<string, string>;
}

export function gerencialDefaultFilterValues(costCenterIds: string[] = []): FilterValues {
  const currentDate = currentLocalDateInput();
  return {
    startDate: currentDate,
    endDate: currentDate,
    costCenter: costCenterIds,
    constructiveUnit: [],
    itemFilter: [],
    quotationFilter: "",
    purchaseRequestFilter: "",
    orderFilter: "",
    supplierFilter: [],
  };
}

/**
 * Período amplo para custo Gerencial acumulado (ex.: avanço financeiro do Chão de Obra).
 * Alinhado ao padrão de mapas de parcelas (`BILLS_INSTALLMENTS_MAP_START_DATE` = 2000-01-01).
 */
export function gerencialCumulativeFilterValues(costCenterIds: string[] = []): FilterValues {
  return {
    ...gerencialDefaultFilterValues(costCenterIds),
    startDate: "2000-01-01",
    endDate: currentLocalDateInput(),
  };
}

/* ─── Escopo do Gerencial: planos financeiros 210, 211, 207, 201, 202 ───── */

/** Ordem de exibição no modal «Total investido por plano» (alinhado ao plano da empresa). */
export const TOTAL_INVESTIDO_PLANO_PREFIXES_ORDERED = [
  "210",
  "211",
  "207",
  "201",
  "202",
] as const;

const TOTAL_INVESTIDO_PREFIX_SET = new Set<string>(TOTAL_INVESTIDO_PLANO_PREFIXES_ORDERED);

function digitsOnly(s: string): string {
  return s.replace(/\D/g, "");
}

/**
 * Retorna o prefixo de capítulo (3 primeiros dígitos) se estiver em 210/211/207/201/202.
 * Usa `accountId` da parcela e, se preciso, o código vindo do mapa payment-categories.
 */
export function getTotalInvestidoPlanoPrefix(
  item: OutcomeItem,
  categoryCodesById: Map<string, string>
): string | null {
  const idRaw = item.accountId?.trim() ?? "";
  const fromId = digitsOnly(idRaw);
  if (fromId.length >= 3) {
    const p = fromId.slice(0, 3);
    if (TOTAL_INVESTIDO_PREFIX_SET.has(p)) return p;
  }
  const codeRaw =
    (item.accountCode?.trim() || "") ||
    (idRaw
      ? categoryCodesById.get(idRaw) || categoryCodesById.get(String(Number(idRaw))) || ""
      : "");
  const fromCode = digitsOnly(String(codeRaw));
  if (fromCode.length >= 3) {
    const p = fromCode.slice(0, 3);
    if (TOTAL_INVESTIDO_PREFIX_SET.has(p)) return p;
  }
  return null;
}

function filterOutcomeForGerencialCusto(
  outcome: OutcomeItem[],
  categoryCodesById: Map<string, string>
): OutcomeItem[] {
  return outcome.filter((item) => getTotalInvestidoPlanoPrefix(item, categoryCodesById) != null);
}

/* ─── Helpers internos ───────────────────────────────────────────────────── */

function getStr(o: Record<string, unknown>, ...keys: string[]): string {
  for (const k of keys) {
    const v = o[k];
    if (v != null) {
      const s = String(v).trim();
      if (s && s !== "0") return s;
    }
  }
  return "";
}

/**
 * Extrai id e nome do enterprise/centro de custo de um objeto de unidade bruto
 * vindo do Sienge. Tenta múltiplos formatos de campo.
 */
function extractCCFromUnit(u: Record<string, unknown>): { id: string; name: string } | null {
  /* Objetos aninhados: enterprise, empreendimento, building, costCenter */
  const nestedKeys = ["enterprise", "empreendimento", "building", "costCenter", "cost_center"];
  for (const key of nestedKeys) {
    const nested = u[key];
    if (nested != null && typeof nested === "object" && !Array.isArray(nested)) {
      const n = nested as Record<string, unknown>;
      const id   = getStr(n, "id", "code", "costCenterId", "enterpriseId");
      const name = getStr(n, "name", "description", "costCenterName", "enterpriseName");
      if (id) return { id, name: name || id };
    }
  }

  /* Campos diretos na unidade */
  const id = getStr(
    u,
    "enterpriseId", "enterprise_id",
    "costCenterId", "cost_center_id",
    "buildingId",   "building_id",
    "empreendimentoId"
  );
  const name = getStr(
    u,
    "enterpriseName", "enterprise_name",
    "costCenterName", "cost_center_name",
    "buildingName",   "building_name"
  );

  return id ? { id, name: name || id } : null;
}

/**
 * Agrupa uma lista bruta de unidades (do Sienge /api/units) por enterprise/CC.
 * Retorna contagem por centroDeId.
 */
function groupUnitsByEnterprise(rawUnits: unknown[]): UnitsByCCItem[] {
  const map = new Map<string, { name: string; count: number }>();

  for (const raw of rawUnits) {
    if (!raw || typeof raw !== "object") continue;
    const unit = raw as Record<string, unknown>;
    const cc = extractCCFromUnit(unit);
    if (!cc || !cc.id) continue;

    if (!map.has(cc.id)) {
      map.set(cc.id, { name: cc.name, count: 0 });
    }
    map.get(cc.id)!.count += 1;
  }

  return Array.from(map.entries())
    .map(([id, { name, count }]) => ({
      costCenterId:   id,
      costCenterName: name,
      unitCount:      count,
    }))
    .filter((x) => x.unitCount > 0)
    .sort((a, b) => b.unitCount - a.unitCount);
}

/* ─── Chamadas de API ────────────────────────────────────────────────────── */

/**
 * Busca contagem de unidades (casas/imóveis) por centro de custo.
 *
 * Tenta primeiro o endpoint dedicado `/api/gerencial/units-by-cc` (backend cacheado).
 * Se indisponível (404 / servidor não reiniciado), faz fallback para
 * `/api/units` (endpoint Sienge já existente) e agrupa localmente.
 */
export async function fetchUnitsByCC(): Promise<UnitsByCCItem[]> {
  /* Tentativa 1: endpoint dedicado (requer reinicialização do servidor) */
  try {
    const { data } = await api.get("/api/gerencial/units-by-cc");
    if (data?.success && Array.isArray(data.data) && data.data.length > 0) {
      return (data.data as Record<string, unknown>[])
        .map((item) => ({
          costCenterId:   String(item.costCenterId   ?? "").trim(),
          costCenterName: String(item.costCenterName ?? "").trim(),
          unitCount:      Number(item.unitCount ?? 0),
        }))
        .filter((x) => x.costCenterId && x.unitCount > 0);
    }
  } catch { /* cai no fallback */ }

  /* Tentativa 2: /api/units existente — agrupa por enterprise/CC */
  try {
    const { data } = await api.get("/api/units", { timeout: 180_000 });
    const rawUnits: unknown[] = Array.isArray(data.data)
      ? data.data
      : Array.isArray(data.results)
        ? data.results
        : [];
    if (rawUnits.length > 0) {
      return groupUnitsByEnterprise(rawUnits);
    }
  } catch { /* sem unidades */ }

  return [];
}

/**
 * Busca os dados completos do Gerencial:
 * - despesas (outcome) filtradas por período e centros de custo
 * - contagem de unidades por CC (tenta dedicated endpoint, depois /api/units)
 *
 * `opts.selectionTypes`: repassado a `fetchOutcome` (ex.: `["P"]` no acumulado do Chão de Obra).
 */
export async function fetchGerencialData(
  filters: FilterValues,
  allCcIds?: string[],
  opts?: { selectionTypes?: Array<"D" | "P"> },
): Promise<GerencialData> {
  const [outcomeRaw, unitsByCC, catMaps] = await Promise.all([
    fetchOutcome(filters, allCcIds, opts),
    fetchUnitsByCC(),
    fetchPaymentCategoryMaps(),
  ]);

  /* Só mantém parcelas do CC solicitado (reforço se a API vier sem filtro ou com tag errada). */
  const selected =
    Array.isArray(filters.costCenter) && filters.costCenter.length > 0
      ? new Set(filters.costCenter.map((id) => String(id).trim()))
      : null;
  const scopedCc =
    selected != null && selected.size > 0
      ? outcomeRaw.filter((item) => selected.has(String(item.costCenterId).trim()))
      : outcomeRaw;

  /* Custo Gerencial: apenas categorias cujo código/id começa em 210, 211, 207, 201 ou 202. */
  const outcome = filterOutcomeForGerencialCusto(scopedCc, catMaps.codes);

  return {
    outcome,
    unitsByCC,
    categoryCodesById: catMaps.codes,
  };
}
